/* Drives the header search flydown from Shopify's Predictive Search API.
   Renders sections/predictive-search.liquid server-side and drops the HTML into
   the theme's existing .search-flydown markup. Below the header's desktop
   breakpoint the whole thing becomes a full-screen takeover. */
(function () {
  var SECTION_ID = 'predictive-search';
  var RESOURCE_TYPES = 'product,collection,page,article';
  var RESOURCE_LIMIT = 8;
  var DEBOUNCE_MS = 250;
  var MIN_TERM_LENGTH = 2;
  var TAKEOVER_QUERY = '(max-width: 1024px)';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function PredictiveSearch(root) {
    this.root = root;
    this.form = root.querySelector('[data-live-search-form]');
    this.input = root.querySelector('[data-live-search-input]');
    this.flydown = root.querySelector('[data-live-search-flydown]');
    this.results = root.querySelector('[data-live-search-results]');
    this.quickLinks = root.querySelector('[data-live-search-quick-links]');
    this.placeholder = root.querySelector('[data-live-search-placeholder]');
    this.cancel = root.querySelector('[data-live-search-takeover-cancel]');
    this.takeoverMedia = window.matchMedia(TAKEOVER_QUERY);
    this.controller = null;
    this.isOpen = false;
    this.isTakeover = false;

    if (!this.input || !this.flydown || !this.results) return;

    this.onInput = debounce(this.search.bind(this), DEBOUNCE_MS);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.root.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    this.takeoverMedia.addEventListener('change', this.onBreakpointChange.bind(this));

    if (this.cancel) this.cancel.addEventListener('click', this.dismiss.bind(this));

    /* The rendered section ends in a submit button, so a click on
       "Search for …" runs the normal form submit. */
    this.results.addEventListener('click', function (event) {
      if (event.target.closest('.tmg-psearch__all')) this.form.requestSubmit();
    }.bind(this));
  }

  PredictiveSearch.prototype.onFocus = function () {
    if (this.takeoverMedia.matches) this.openTakeover();
    if (this.input.value.trim().length >= MIN_TERM_LENGTH) this.search();
    else if (this.quickLinks) this.open(this.quickLinks);
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') this.dismiss();
  };

  PredictiveSearch.prototype.onDocumentClick = function (event) {
    if ((this.isOpen || this.isTakeover) && !this.root.contains(event.target)) this.dismiss();
  };

  PredictiveSearch.prototype.onBreakpointChange = function () {
    if (!this.takeoverMedia.matches) this.closeTakeover();
  };

  PredictiveSearch.prototype.search = function () {
    var terms = this.input.value.trim();

    if (terms.length < MIN_TERM_LENGTH) {
      this.close();
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    var url =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/') +
      'search/suggest?q=' +
      encodeURIComponent(terms) +
      '&resources[type]=' +
      RESOURCE_TYPES +
      '&resources[limit]=' +
      RESOURCE_LIMIT +
      '&section_id=' +
      SECTION_ID;

    fetch(url, { signal: this.controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(
        function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var section = parsed.querySelector('.shopify-section');
          this.results.innerHTML = section ? section.innerHTML : html;
          this.open(this.results);
        }.bind(this)
      )
      .catch(
        function (error) {
          if (error.name !== 'AbortError') this.close();
        }.bind(this)
      );
  };

  /* Visibility is driven by the theme's own data-animation-state styles, so the
     flydown keeps its height transition. */
  PredictiveSearch.prototype.open = function (panel) {
    [this.results, this.quickLinks, this.placeholder].forEach(function (el) {
      if (el) el.dataset.animationState = el === panel ? 'visible' : 'closed';
    });

    this.flydown.dataset.animationState = 'open';
    this.flydown.style.setProperty('--open-height', panel.scrollHeight + 'px');
    this.root.classList.add('live-search--active');
    this.isOpen = true;
  };

  PredictiveSearch.prototype.close = function () {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }

    this.flydown.dataset.animationState = 'closed';
    this.flydown.style.removeProperty('--open-height');
    [this.results, this.quickLinks, this.placeholder].forEach(function (el) {
      if (el) el.dataset.animationState = 'closed';
    });
    this.root.classList.remove('live-search--active');
    this.isOpen = false;
  };

  PredictiveSearch.prototype.openTakeover = function () {
    if (this.isTakeover) return;
    this.root.classList.add('tmg-psearch--takeover');
    document.body.classList.add('tmg-psearch-open');
    this.isTakeover = true;
  };

  PredictiveSearch.prototype.closeTakeover = function () {
    if (!this.isTakeover) return;
    this.root.classList.remove('tmg-psearch--takeover');
    document.body.classList.remove('tmg-psearch-open');
    this.isTakeover = false;
  };

  PredictiveSearch.prototype.dismiss = function () {
    this.close();
    if (this.isTakeover) {
      this.closeTakeover();
      this.input.blur();
    }
  };

  function init() {
    document.querySelectorAll('[data-live-search]').forEach(function (root) {
      if (root.dataset.predictiveSearchInit) return;
      root.dataset.predictiveSearchInit = '1';
      new PredictiveSearch(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
